import '.a{display:grid;}.ssr-plugin'
import '.i7{grid-template-columns:repeat(2, minmax(250px, 1fr));}.ssr-plugin'
import '.jc{column-gap:1.5rem;}.ssr-plugin'
import '.jd{row-gap:1rem;}.ssr-plugin'
import Column from '@app/components/layout/Column'
import useMediaQuery from '@app/util/useMediaQuery'
import { SectionHeader } from '@app/components/blocks/sectionHeader/SectionHeader'
import { useLanguage } from '@i18n/useLanguage'
import Row from '@app/components/layout/Row'
import { InitiativeCard } from '@app/components/blocks/card/InitiativeCard'
import { i18n } from '@i18n/index'
import { useMemo, useState } from 'react'
import { SortingDropdown } from '@app/components/core/dropdown/SortingDropdown'
import { enrich } from '@i18n/localizeHelper'
import { pages } from '@app/util/links'
import type {
  RegionalInitiativeSortOrder,
  RegionalListInitiativeModel,
} from 'functions/app/initiative/list-regional'
import { useQuery } from '@based/sdk'
import defaultProjectPicture from '@app/assets/defaultProjectPicture.png'
import { styled } from 'inlines'
import type { GeoViewModel } from './models'
import { labelAddressBuilder } from '@app/util/helpers'
import { Empty } from '@app/components/blocks/empty/Empty'
import BuilderImage from '@app/assets/images/builder.png'

export const Initiatives = ({ entity }: { entity: GeoViewModel }) => {
  const isRegion = 'municipalities' in entity
  const isMobile = useMediaQuery()
  const largerGap = isMobile ? '2rem' : '1.875rem'
  const [language, _] = useLanguage()
  const translations = i18n.languages[language].app
  const sortingTranslations = translations.regionPage.sorting
  const addressListingContext = 'regional'

  const [sortOrder, setSortOrder] =
    useState<RegionalInitiativeSortOrder>('newest')

  const listPayload = isRegion
    ? { regionId: entity.id, sortOrder }
    : { municipalityId: entity.id, sortOrder }
  const initiativeQuery = useQuery('list-regional-initiative', listPayload)
  const initiatives = useMemo(
    () => (initiativeQuery.data ?? []) as RegionalListInitiativeModel[],
    [initiativeQuery.data],
  )

  const followerCountQuery = useQuery('count-followers', {
    entityType: 'initiative',
    entityIds:
      sortOrder === 'popular'
        ? initiatives.map((initiative) => initiative.id)
        : [],
  })

  const displayedInitiatives = useMemo(() => {
    if (sortOrder !== 'popular') {
      return initiatives
    }
    const counts = followerCountQuery.data ?? []
    return [...initiatives]
      .map((initiative, index) => ({
        ...initiative,
        followerCount: counts[index] ?? 0,
      }))
      .sort((a, b) => b.followerCount - a.followerCount)
  }, [initiatives, sortOrder, followerCountQuery.data])

  const sortingOptions = [
    { value: 'newest', label: sortingTranslations.newestFirst },
    { value: 'oldest', label: sortingTranslations.oldestFirst },
    { value: 'popular', label: sortingTranslations.popular },
  ]

  const title = enrich(
    translations.regionPage.initiativesIn,
    entity.name,
    initiatives.length,
  )

  return (
    <Column gap={largerGap} alignItems="stretch">
      <SectionHeader title={title} />

      <Row style={{ gap: '0.75rem', alignItems: 'center' }}>
        <SortingDropdown
          options={sortingOptions}
          selectedValue={sortOrder}
          onSelect={(value) =>
            setSortOrder(value as RegionalInitiativeSortOrder)
          }
        />
      </Row>
      {displayedInitiatives.length === 0 ? (
        <Empty
          title={translations.regionPage.noInitiativesTitle}
          imageSrc={BuilderImage}
          ctaLabel={translations.regionPage.noInitiativesCta}
          ctaLink={pages.dashboardStartInitiative}
        >
          {translations.regionPage.noInitiativesDescription}
        </Empty>
      ) : (
        <styled.div className="a i7 jc jd"
          style={{
            '@media (max-width: 1200px)': {
              gridTemplateColumns: 'repeat(1, minmax(250px, 1fr))',
            },}}
        >
          {displayedInitiatives.map((initiative) => (
            <InitiativeCard
              key={initiative.slug}
              to={pages.initiative(initiative.slug)}
              image={initiative.images?.[0] ?? defaultProjectPicture}
              title={initiative.title}
              description={initiative.description}
              location={labelAddressBuilder(
                initiative.location.address,
                translations.common.notSpecified,
                addressListingContext,
              )}
              phase={initiative.phase?.name}
              tagText={
                initiative.searchingForMembers
                  ? translations.common.initiativeStatus.searchingForMembers
                  : translations.common.initiativeStatus.notSearchingForMembers
              }
              tagVariant={
                initiative.searchingForMembers ? 'success' : 'neutral'
              }
            />
          ))}
        </styled.div>
      )}
    </Column>
  )
}
